import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { bulkImportDeviceMappings, getDevices } from '../api/alertApi';

export default function ImportLegacyDevicesModal({ templates, unmappedDeviceIds, onClose, onImported }) {
  const [selected, setSelected] = useState(() => new Set(unmappedDeviceIds));
  const [templateId, setTemplateId] = useState(templates[0]?.id ? String(templates[0].id) : '');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sendTime, setSendTime] = useState('');

  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: getDevices });
  const devices = devicesQuery.data?.data || [];
  const importCandidates = useMemo(
    () => devices.filter((d) => unmappedDeviceIds.includes(d.device_id)),
    [devices, unmappedDeviceIds]
  );

  function toggle(deviceId) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(deviceId)) next.delete(deviceId); else next.add(deviceId);
      return next;
    });
  }

  function toggleAll() {
    setSelected((current) => (current.size === importCandidates.length ? new Set() : new Set(importCandidates.map((d) => d.device_id))));
  }

  const importMutation = useMutation({
    mutationFn: () => {
      if (!selected.size) throw new Error('Select at least one device');
      if (!templateId) throw new Error('Please select a template');
      return bulkImportDeviceMappings({
        device_ids: Array.from(selected),
        template_id: Number(templateId),
        email: email.trim(),
        phone_number: phone.trim(),
        send_time: sendTime || null,
      });
    },
    onSuccess: (result) => {
      toast.success(`Imported ${result.importedCount} device${result.importedCount === 1 ? '' : 's'}`);
      onImported();
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Import failed'),
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Import Legacy Devices</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
              These devices exist in the old device registry (used by the Dashboard) but have no
              mapping yet in this module. Pick a template and recipients to create one mapping per
              selected device.
            </div>
          </div>
          <button style={closeButton} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={modalBody}>
          {!importCandidates.length ? (
            <div style={{ color: '#64748b', fontSize: 14 }}>
              Nothing to import — every legacy device already has a mapping.
            </div>
          ) : (
            <>
              <div style={fieldGrid}>
                <label style={field}>
                  <span style={label}>Template to apply</span>
                  <select style={input} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                    <option value="">Select a template</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.source_type})</option>
                    ))}
                  </select>
                </label>
                <label style={field}>
                  <span style={label}>Send Time (IST, optional)</span>
                  <input type="time" style={input} value={sendTime} onChange={(e) => setSendTime(e.target.value)} />
                </label>
                <label style={field}>
                  <span style={label}>Email (optional, applied to all selected)</span>
                  <input style={input} placeholder="ops@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label style={field}>
                  <span style={label}>WhatsApp number (optional, applied to all selected)</span>
                  <input style={input} placeholder="919999999999" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </label>
              </div>

              <div style={{ marginTop: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                    Devices to import ({selected.size}/{importCandidates.length} selected)
                  </div>
                  <button style={linkButton} onClick={toggleAll}>
                    {selected.size === importCandidates.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div style={deviceList}>
                  {importCandidates.map((d) => (
                    <label key={d.device_id} style={deviceRow}>
                      <input type="checkbox" checked={selected.has(d.device_id)} onChange={() => toggle(d.device_id)} style={{ width: 16, height: 16 }} />
                      <code>{d.device_id}</code>
                      <span style={{ color: '#64748b' }}>{d.name}{d.location ? ` · ${d.location}` : ''}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <button style={primaryButton} onClick={() => importMutation.mutate()} disabled={importMutation.isPending}>
                  {importMutation.isPending ? 'Importing…' : `Import ${selected.size} Device${selected.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 20px',
  overflowY: 'auto',
  zIndex: 1000,
};

const modal = {
  background: '#f8fbff',
  borderRadius: 24,
  width: 640,
  maxWidth: '100%',
  boxShadow: '0 30px 70px rgba(15, 23, 42, 0.3)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100vh - 80px)',
};

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'start',
  gap: 16,
  padding: '22px 24px',
  borderBottom: '1px solid #dbe4f0',
};

const modalBody = {
  padding: 24,
  overflowY: 'auto',
};

const closeButton = {
  border: 'none',
  background: '#eef2f7',
  color: '#334155',
  width: 32,
  height: 32,
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '32px',
  padding: 0,
  flexShrink: 0,
};

const fieldGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

const field = {
  display: 'grid',
  gap: 6,
};

const label = {
  color: '#334155',
  fontSize: 13,
  fontWeight: 600,
};

const input = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #cbd5e1',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 14,
  color: '#0f172a',
  background: '#fff',
};

const primaryButton = {
  border: 'none',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
};

const linkButton = {
  border: 'none',
  background: 'none',
  color: '#0284c7',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const deviceList = {
  display: 'grid',
  gap: 8,
  maxHeight: 260,
  overflowY: 'auto',
  border: '1px solid #dbe4f0',
  borderRadius: 12,
  padding: 10,
  background: '#fff',
};

const deviceRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 13,
  padding: '4px 2px',
  cursor: 'pointer',
};
